import { type FC } from 'react';
import { Card } from '../Card';
import { CompletedTrainingCard } from './components/CompletedTrainingCard';
import styled from 'styled-components';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { useState } from 'react';
import { LabelledToggle } from '../Toggle';
import type { LabelledToggleOption } from '../Toggle/LabelledToggle/LabelledToggle';
import { useManagerTeamCampaignsQuery, type ManagerTeamCampaign } from '../../apiHooks/useManagerTeamCampaignsQuery';
import { ManagerTeamTrainingCard } from './components/ManagerTeamTrainingCard';
import { Tooltip } from '../Tooltip';
import { Icon, IconType } from '../Props/Icon/Icon';

export const ManagerTeamTrainingWidget: FC = () => {
  const { data: managerListTrainingCampaigns } = useManagerTeamCampaignsQuery();

  const { mandatoryCampaigns, completedCampaigns } = (managerListTrainingCampaigns || []).reduce(
    (
      acc: { mandatoryCampaigns: ManagerTeamCampaign[]; completedCampaigns: ManagerTeamCampaign[] },
      campaign: ManagerTeamCampaign,
    ) => {
      if (campaign.campaignStatus === 'completed') {
        acc.completedCampaigns.push(campaign);
      } else {
        acc.mandatoryCampaigns.push(campaign);
      }
      return acc;
    },
    { mandatoryCampaigns: [], completedCampaigns: [] },
  );

  const [selectedTrainingType, setSelectedTrainingType] = useState<'mandatory' | 'completed'>('mandatory');

  const toggleLabels: LabelledToggleOption<'mandatory' | 'completed'>[] = [
    {
      label: <NamedContentEntryText container="span" refKey="yourTraining" subKey="mandatory" />,
      value: 'mandatory',
      selected: selectedTrainingType === 'mandatory',
    },
    {
      label: <NamedContentEntryText container="span" refKey="yourTraining" subKey="completed" />,
      value: 'completed',
      selected: selectedTrainingType === 'completed',
    },
  ];

  const onFilterChange = (index: number) => {
    setSelectedTrainingType(toggleLabels[index]?.value ?? 'mandatory');
  };

  return (
    <Card
      title={<NamedContentEntryText container={'span'} refKey="managerSectionTitles" subKey="cybersecurityTraining" />}
      rightElem={
        <Tooltip
          variant="dark"
          offset={15}
          placement="top"
          content={
            <TooltipInfoContainer>
              <NamedContentEntryText
                container={'span'}
                refKey="managerTeamCyberSecurityTraining"
                subKey="description"
              />
            </TooltipInfoContainer>
          }
        >
          <Icon icon={IconType.INFO_TOOLTIP} width={16} height={16} fill="transparent" />
        </Tooltip>
      }
    >
      <FiltersBar>
        <LabelledToggle
          fullWidth
          labels={toggleLabels}
          onSelect={(selectedIndex: number) => onFilterChange(selectedIndex)}
        />
      </FiltersBar>
      <SectionContent>
        {selectedTrainingType === 'mandatory' &&
          mandatoryCampaigns.map((campaign: ManagerTeamCampaign) => (
            <ManagerTeamTrainingCard
              key={campaign.campaignId}
              title={campaign.campaignName}
              learnersCompletedTotal={campaign.completed || 0}
              totalLearners={campaign.enrolled || 0}
              dueDate={calculateDueDate(campaign.startDate, campaign.durationDays)}
              estimatedCompletionTime={(campaign.mandatoryModules.length + campaign.dynamicModules.length) * 5}
              nudgeActionText={
                <NamedContentEntryText container="span" refKey="managerTeamCyberSecurityTraining" subKey="nudge" />
              }
              campaignId={campaign.campaignId}
            />
          ))}
        {selectedTrainingType === 'completed' &&
          completedCampaigns.map((campaign: ManagerTeamCampaign) => (
            <CompletedTrainingCard
              key={campaign.campaignId}
              campaignUrl={campaign.campaignUrl}
              name={campaign.campaignName}
            />
          ))}
      </SectionContent>
    </Card>
  );
};

const calculateDueDate = (startDate: string, durationDays: number): string => {
  const start = new Date(startDate);
  start.setDate(start.getDate() + durationDays);
  return start.toISOString();
};

const FiltersBar = styled.div`
  display: flex;
  width: 100%;
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 10px;
`;

const TooltipInfoContainer = styled.div`
  padding: 8px;
  width: 280px;
`;
